import React from "react";
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { Check, Close, ContentCopy, Link as LinkIcon } from "@mui/icons-material";
import { Product } from "../../../types";
import { ProductThumbnail } from "../../../components/common/ProductThumbnail";
import { getEffectivePrice } from "../../../utils/productPricing";
import {
    availableStock,
    productCardText,
    stockTone,
    stockWords,
    storefrontLink,
    taka,
    weightText,
} from "../answerDeskUtils";

interface ProductContextCardProps {
    product: Product;
    compact?: boolean;
    copiedKey: string | null;
    onCopy: (key: string, text: string) => void;
    onRemove: (product: Product) => void;
}

// Live facts for one product in the chat — price, stock, link — so
// moderators never type a number from memory.
export const ProductContextCard: React.FC<ProductContextCardProps> = ({
    product,
    compact,
    copiedKey,
    onCopy,
    onRemove,
}) => {
    const link = storefrontLink(product);
    const effective = getEffectivePrice(product);
    const onSale = !!product.is_on_sale && Number(product.selling_price) > effective;
    const available = availableStock(product);
    const tone = stockTone(product);
    const meta = [product.brand?.name, product.category?.name, weightText(product)].filter(Boolean).join(" · ");
    const cardKey = `card:${product.product_id}`;
    const linkKey = `link:${product.product_id}`;

    return (
        <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 3, display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {!compact && <ProductThumbnail images={product.images} size={56} />}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                        {product.name}
                    </Typography>
                    {meta && (
                        <Typography variant="caption" color="text.secondary">
                            {meta}
                        </Typography>
                    )}
                </Box>
                <Tooltip title="Remove from this chat">
                    <IconButton size="small" onClick={() => onRemove(product)} aria-label={`Remove ${product.name} from this chat`}>
                        <Close fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontSize: compact ? 18 : 22, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                    {taka(effective)}
                </Typography>
                {onSale && (
                    <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                        {taka(product.selling_price)}
                    </Typography>
                )}
                <Chip
                    size="small"
                    variant="outlined"
                    color={tone}
                    label={
                        tone === "success" && available !== null
                            ? `In stock · ${available}`
                            : stockWords(product).replace(/^./, (c) => c.toUpperCase())
                    }
                    sx={{ fontWeight: 500 }}
                />
                {!link && (
                    <Chip size="small" label="Not on the website" sx={{ bgcolor: "action.hover" }} />
                )}
            </Stack>

            {!compact && (
                <Box
                    sx={{
                        bgcolor: "action.hover",
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        fontSize: 13,
                        lineHeight: 1.5,
                        whiteSpace: "pre-line",
                        wordBreak: "break-all",
                        color: "text.secondary",
                    }}
                >
                    {productCardText(product)}
                </Box>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                    size="small"
                    variant={compact ? "outlined" : "contained"}
                    disableElevation
                    color={copiedKey === cardKey ? "success" : "primary"}
                    startIcon={copiedKey === cardKey ? <Check /> : <ContentCopy fontSize="small" />}
                    onClick={() => onCopy(cardKey, productCardText(product))}
                    sx={{ minHeight: 36, flex: compact ? "0 0 auto" : "1 1 auto" }}
                >
                    {copiedKey === cardKey ? "Copied" : compact ? "Copy card" : "Copy product card"}
                </Button>
                {link && (
                    <Button
                        size="small"
                        variant="outlined"
                        color={copiedKey === linkKey ? "success" : "primary"}
                        startIcon={copiedKey === linkKey ? <Check /> : <LinkIcon fontSize="small" />}
                        onClick={() => onCopy(linkKey, link)}
                        sx={{ minHeight: 36 }}
                    >
                        {copiedKey === linkKey ? "Copied" : "Copy link"}
                    </Button>
                )}
            </Stack>
        </Paper>
    );
};
